import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, Search, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const AttachmentTypesTab = ({ api, attachmentTypes, equipmentTypes, fetchAttachmentTypes }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    equipment_type_ids: []
  });

  const activeCount = attachmentTypes.filter(t => t.is_active).length;
  const customCount = attachmentTypes.filter(t => !t.is_system).length;

  const filteredTypes = attachmentTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setForm({ name: '', description: '', is_active: true, equipment_type_ids: [] });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        is_active: type.is_active,
        equipment_type_ids: type.equipment_type_ids || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/attachment-types/${editingType.id}`, form);
        toast.success('Attachment type updated');
      } else {
        await api.post('/attachment-types', form);
        toast.success('Attachment type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchAttachmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type) => {
    if (type.is_system) {
      toast.error('System types cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete attachment type "${type.name}"?`)) return;
    try {
      await api.delete(`/attachment-types/${type.id}`);
      toast.success('Attachment type deleted');
      fetchAttachmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await api.put(`/attachment-types/${type.id}`, {
        name: type.name,
        description: type.description,
        is_active: !type.is_active
      });
      toast.success(`Attachment type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchAttachmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const getEquipmentTypeNames = (ids) => {
    if (!ids || ids.length === 0) return 'All Assets';
    return ids.map(id => equipmentTypes.find(t => t.id === id)?.name || id).join(', ');
  };

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Attachment Types</CardTitle>
              <CardDescription>
                {activeCount} active, {customCount} custom attachment types
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-attachment-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Attachment
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="attachment-search"
              placeholder="Search attachments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="label-overline">Name</TableHead>
                <TableHead className="label-overline">Type</TableHead>
                <TableHead className="label-overline">Compatible Assets</TableHead>
                <TableHead className="label-overline">Status</TableHead>
                <TableHead className="label-overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTypes.map((type) => (
                <TableRow key={type.id} data-testid={`attachment-row-${type.id}`}>
                  <TableCell className="font-medium">{type.name}</TableCell>
                  <TableCell>
                    {type.is_system ? (
                      <Badge variant="secondary">System</Badge>
                    ) : (
                      <Badge className="bg-[#ffd108]/20 text-[#ffd108]">Custom</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {getEquipmentTypeNames(type.equipment_type_ids)}
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={type.is_active}
                      onCheckedChange={() => handleToggleActive(type)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(type)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {!type.is_system && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(type)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Attachment Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Attachment Type' : 'Add Attachment Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="attachment-form">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Ripper, Bucket"
                required
                disabled={editingType?.is_system}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Compatible Equipment Types</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Leave empty for all types, or select specific types
              </p>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                {equipmentTypes.filter(t => t.is_active).map(type => (
                  <div key={type.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`eq-${type.id}`}
                      checked={form.equipment_type_ids.includes(type.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setForm({ 
                            ...form, 
                            equipment_type_ids: [...form.equipment_type_ids, type.id] 
                          });
                        } else {
                          setForm({ 
                            ...form, 
                            equipment_type_ids: form.equipment_type_ids.filter(id => id !== type.id) 
                          });
                        }
                      }}
                    />
                    <Label htmlFor={`eq-${type.id}`} className="text-sm font-normal">
                      {type.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttachmentTypesTab;
